import SiteInertiaController from '@/actions/App/Http/Controllers/SiteInertiaController';
import { cn } from '@/lib/utils';
import { Link } from '@inertiajs/react';
import { ArrowUpRight, MapPin, Ticket } from 'lucide-react';
import { type NavGroup, type NavLink } from './use-nav-items';

interface NavMegaPanelProps {
  id: string;
  groups: NavGroup[];
  isOpen: boolean;
  /**
   * False until the panel has been opened once. The panel itself is always mounted so its
   * enter transition plays on the *first* open too — but thumbnails stay unmounted until
   * then, so a header on every page doesn't pull artwork nobody asked for.
   */
  showMedia: boolean;
  onNavigate: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

/**
 * N11 mega-menu panel.
 *
 * Rendered *inside* the header's morphing inner wrapper, so it inherits the pill's width
 * and offset for free and stays anchored through the scroll morph — no position maths.
 *
 * It stays mounted while closed (behind `invisible`) so both transitions can play;
 * `invisible` also keeps its links out of the tab order when closed.
 */
export function NavMegaPanel({ id, groups, isOpen, showMedia, onNavigate, onMouseEnter, onMouseLeave }: NavMegaPanelProps) {
  return (
    <div
      id={id}
      role="region"
      aria-label="Menu điều hướng"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cn(
        'absolute inset-x-0 top-full mt-2 origin-top overflow-hidden rounded-3xl border border-gray-100 bg-white/98 shadow-2xl ring-1 ring-black/5 backdrop-blur-xl',
        // Same 520ms curve as the shell morph — two timings would read as two animations.
        'transition-[opacity,transform,visibility] duration-[520ms] ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none',
        isOpen ? 'visible translate-y-0 opacity-100' : 'invisible -translate-y-2.5 opacity-0',
      )}
    >
      <div className="grid max-h-[60vh] gap-x-8 gap-y-6 overflow-y-auto p-6 md:grid-cols-3 lg:grid-cols-4 lg:p-8">
        {groups.slice(0, 3).map((group) => (
          <div key={group.location} className="min-w-0 space-y-2">
            <p className="text-primary px-2 text-[11px] font-bold tracking-[0.12em] uppercase">{group.location}</p>
            <ul className="space-y-1">
              {group.links.map((link) => (
                <li key={link.key}>
                  <NavMegaCard link={link} showMedia={showMedia} onNavigate={onNavigate} />
                </li>
              ))}
            </ul>
          </div>
        ))}

        <PromoCell onNavigate={onNavigate} />
      </div>
    </div>
  );
}

function NavMegaCard({ link, showMedia, onNavigate }: { link: NavLink; showMedia: boolean; onNavigate: () => void }) {
  const content = (
    <>
      <span className="relative size-14 shrink-0 overflow-hidden rounded-xl bg-[var(--surface-muted)]">
        {link.image && showMedia ? (
          <img
            src={link.image}
            alt=""
            loading="lazy"
            decoding="async"
            className="size-full object-cover transition-transform duration-500 group-hover/card:scale-110 motion-reduce:transition-none"
          />
        ) : (
          <span className="flex size-full items-center justify-center text-gray-400">
            <MapPin className="size-5" aria-hidden="true" />
          </span>
        )}
      </span>

      <span className="min-w-0 flex-1">
        <span className="group-hover/card:text-primary flex items-center gap-1 text-sm font-semibold text-gray-900 transition-colors">
          <span className="truncate">{link.title}</span>
          {link.isExternal && <ArrowUpRight className="size-3.5 shrink-0 text-gray-400" aria-hidden="true" />}
        </span>
        {link.description && <span className="mt-0.5 line-clamp-1 block text-xs text-gray-500">{link.description}</span>}
      </span>
    </>
  );

  const className = cn(
    'group/card focus-visible:ring-ring flex items-center gap-3 rounded-2xl p-2 transition-colors duration-200',
    'hover:bg-[var(--surface-muted)] focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none',
  );

  // External destinations must be a real navigation — an Inertia visit would try to
  // fetch another origin as a page component and fail.
  if (link.isExternal) {
    return (
      <a href={link.href} target="_blank" rel="noopener noreferrer" className={className} onClick={onNavigate}>
        {content}
      </a>
    );
  }

  return (
    <Link href={link.href} className={className} onClick={onNavigate}>
      {content}
    </Link>
  );
}

function PromoCell({ onNavigate }: { onNavigate: () => void }) {
  return (
    <Link
      href={SiteInertiaController.search().url}
      onClick={onNavigate}
      className="group/promo focus-visible:ring-ring bg-primary relative flex min-h-[180px] flex-col justify-between overflow-hidden rounded-2xl p-5 text-white focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
    >
      {/* Two soft light sources give the flat brand red some depth without an image. */}
      <span aria-hidden="true" className="absolute -top-16 -right-10 size-40 rounded-full bg-white/20 blur-2xl" />
      <span aria-hidden="true" className="absolute -bottom-14 -left-8 size-32 rounded-full bg-black/10 blur-2xl" />

      <span className="relative flex size-10 items-center justify-center rounded-full bg-white/20">
        <Ticket className="size-5" aria-hidden="true" />
      </span>

      <span className="relative block">
        <span className="block text-lg leading-snug font-bold">Đặt vé ngay</span>
        <span className="mt-1 block text-sm text-white/85">Chọn điểm đến và ngày đi để xem giá tốt nhất</span>
        <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold">
          Bắt đầu
          <ArrowUpRight className="size-4 transition-transform duration-200 group-hover/promo:translate-x-0.5 group-hover/promo:-translate-y-0.5 motion-reduce:transition-none" />
        </span>
      </span>
    </Link>
  );
}
